/**
 * Flags evaluated through Datadog.
 *
 * New flags belong here. The flags already in `utils/launchdarkly` keep
 * evaluating through LaunchDarkly exactly as before — nothing in this
 * directory changes how they initialise or resolve.
 *
 * The enum is what keeps flag keys compile-time symbols: a typo is a build
 * error rather than a flag that silently reads its fallback forever, and
 * `datadogFlagFallbacks` is a mapped type, so adding a member here without
 * declaring what it falls back to does not compile.
 */
export enum DatadogFlag {
  /**
   * Carries no product behaviour of its own. It exists so that the whole
   * path — provider construction, evaluation context, targeting rule — can be
   * exercised against a rule authored in Datadog before anything anybody
   * depends on is put behind it.
   */
  FLAG_EVALUATION_CANARY = 'datadog-flag-evaluation-canary-web',

  /**
   * Show content cards on the homepage, served from the Experience subgraph
   * and read from the surface the app reads.
   *
   * The single gate. Off is the Sanity-authored marketing row, which is what
   * production serves today, so this ships inert and the ramp is the launch.
   * On, every guest is served the same way — consent decides whether the
   * subgraph may personalise the read, not where the read goes.
   *
   * One key rather than three deliberately. Separate keys for "Adobe or
   * Sanity", "subgraph or Alloy", and "which surface" buy the ability to hold
   * one axis while moving another, which is only worth its cost if they have
   * to move on different days; these do not. What has to hold is that off is
   * a complete rollback to a row guests can see, and it is.
   *
   * The cost of collapsing them is that a delivery fault rolls back to no
   * content cards rather than to Alloy-delivered ones. That is free only
   * while production has no content cards to lose, which is true today and
   * stops being true the first time this reaches 100%. Re-separating the
   * Adobe-or-Sanity axis before then is the intended follow-up, not a
   * regression to discover later.
   *
   * No platform suffix, because the app is expected to read this key too and a
   * rollout percentage then has to mean one population across both clients. It
   * does not read it yet — the app still fetches through the Adobe mobile SDK
   * on-device — so today this ramps web alone. Bucketing is already aligned
   * for that migration: both clients derive the targeting key from the bare
   * loyalty id.
   */
  SERVER_SIDE_CONTENT_CARD_DELIVERY = 'enable-server-side-content-card-delivery',
}

/**
 * Flags whose variations are numbers. Empty today; declared so that adding
 * one is a single-line change in the same shape the LaunchDarkly registry
 * uses, rather than a reshaping of the type.
 */
export type NumericDatadogFlags = never;

/** Flags whose variations are strings. */
export type StringDatadogFlags = never;

/** Everything else. */
export type BooleanDatadogFlags = Exclude<DatadogFlag, NumericDatadogFlags | StringDatadogFlags>;

export type DatadogFlagType<F extends DatadogFlag> = F extends NumericDatadogFlags
  ? number
  : F extends StringDatadogFlags
  ? string
  : F extends BooleanDatadogFlags
  ? boolean
  : never;

/**
 * The value a flag takes when the provider has nothing to say about it —
 * because the rollout has not reached this guest, because the flag was never
 * authored, or because the configuration could not be fetched. Every flag
 * needs one, so there is no code path where "we could not ask" and "the
 * answer is undefined" look the same to a caller.
 */
export const datadogFlagFallbacks: { [F in DatadogFlag]: DatadogFlagType<F> } = {
  [DatadogFlag.FLAG_EVALUATION_CANARY]: false,
  // Falls back to the pre-ramp behaviour, so a guest the rollout has not
  // reached and a guest whose configuration fetch failed get the same thing:
  // what shipped before the flag existed.
  [DatadogFlag.SERVER_SIDE_CONTENT_CARD_DELIVERY]: false,
};
